import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button } from 'components/Atom/Button';
import Select from 'components/Atom/Select';
import Input from 'components/Atom/Input';
import { useSelector } from 'react-redux';
import './style.css';
import { Form, Checkbox, Tooltip, Switch, Button as ANTDButton, notification, Divider } from 'antd';
import { getGateways } from '../../../api/gateways';
import { COUNTRIES } from 'utils/constant';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { createRule, updateRule } from 'api/routingRules';
import { checkIsArraysAreSame, SUB_CurrencyNames } from 'utils/services';
const RoutingRuleForm = ({ ruleData, handleClose, updateRuleData }) => {
  const store = useSelector((state) => state?.company?.store);
  const [gatewaysData, setGatewaysData] = useState();
  const [instanceOptions, setInstanceOptions] = useState();
  const [failureInstanceOptions, setFailureInstanceOptions] = useState();
  const [isGettingGatewaysData, setIsGettingGatewaysData] = useState(false);
  const [formFieldsOptions, setFormFieldsOptions] = useState({ AMOUNT: true, CURRENCY: false, LOCATION: false, BINS: false, ATTRIBUTES: false, EXCHANGE_RATE: false });
  const [binsOptions, setBinsOptions] = useState([]);
  const [attributesOptions, setAttributesOptions] = useState([]);
  const [customOptionValue, setCustomOptionValue] = useState('');
  const customOptionInputRef = useRef(null);
  const [isLoading, setLoading] = useState(false);
  const [isPaymentBaseConversion, setIsPaymentBaseConversion] = useState(ruleData?.exchange_rate?.is_payment_base_conversion);
  const [isLocationExcluded, setIsLocationExcluded] = useState(ruleData?.is_location_excluded);
  const [form] = Form.useForm();
  const initialFormData = {
    ...ruleData,
    failure_instance_id: ruleData?.on_failure_instance_id,
    currency_data: ruleData?.currency || undefined,
    location_data: ruleData?.location || undefined,
    bins_data: ruleData?.bins || undefined,
    rule_attributes: ruleData?.attributes || undefined,
    amount_value: ruleData?.amount?.value,
    operator: ruleData?.amount?.operator,
    rangeFrom: ruleData?.amount?.rangeFrom,
    rangeTo: ruleData?.amount?.rangeTo,
    payment_currency: ruleData?.exchange_rate?.payment_currency?.toUpperCase() || 'USD',
    order_currency: ruleData?.exchange_rate?.order_currency?.toUpperCase() || 'PKR',
    exchange_rate_amount: ruleData?.exchange_rate?.amount,
  };
  useEffect(() => {
    if (ruleData) {
      if (ruleData?.attributes) {
        const attributesValues = ruleData?.attributes?.map((value) => {
          return {
            label: value,
            value,
          };
        });
        setAttributesOptions(attributesValues);
      }
      if (ruleData?.bins) {
        const binsValues = ruleData?.bins?.map((value) => {
          return {
            label: value,
            value,
          };
        });
        setBinsOptions(binsValues);
      }
      setFormFieldsOptions({
        AMOUNT: !!ruleData?.amount?.operator,
        CURRENCY: !!ruleData?.currency,
        LOCATION: !!ruleData?.location,
        BINS: !!ruleData?.bins,
        ATTRIBUTES: !!ruleData?.attributes,
        EXCHANGE_RATE: !!ruleData?.exchange_rate?.amount,
      });
    }
  }, []);
  const handleGatewaysData = async () => {
    setIsGettingGatewaysData(true);
    try {
      const response = await getGateways(store?._id);
      const data = [];
      if (response && Array.isArray(response)) {
        for (let i = 0; i < response?.length; i++) {
          const item = response[i];
          if (['card', undefined].includes(item?.payment_method)) {
            data.push({
              label: item?.name,
              value: item?._id,
            });
          }
        }
      }
      setGatewaysData(data);
      setInstanceOptions(data);

      if (ruleData) {
        const filtered = data?.filter((g) => g?.value !== ruleData?.instance_id);
        setFailureInstanceOptions(filtered);

        const mappedFailureValues = (ruleData?.on_failure_instance_id ?? []).map((id) => data?.find((g) => g?.value === id)).filter(Boolean);

        form.setFieldsValue({
          failure_instance_id: filtered?.length > 1 ? mappedFailureValues : (mappedFailureValues[0] ?? undefined),
        });
      } else {
        setFailureInstanceOptions(data);
      }
    } catch (err) {}
    setIsGettingGatewaysData(false);
  };
  useEffect(() => {
    handleGatewaysData();
  }, [store]);

  const locationsOptions = COUNTRIES.map((value) => ({
    label: value,
    value: value?.toLowerCase(),
  }));
  const amountOptions = [
    {
      label: 'Less',
      value: 'less',
    },
    {
      label: 'Greater',
      value: 'greater',
    },
    {
      label: 'Range',
      value: 'range',
    },
    {
      label: 'Less than or Equal',
      value: 'less_than_equal',
    },
    {
      label: 'Greater than or Equal',
      value: 'greater_than_equal',
    },
  ];
  const paymentRetryStrategyOptions = [
    {
      label: 'Loop',
      value: 'loop',
    },
    {
      label: 'Retain',
      value: 'retain',
    },
  ];
  const handleInstanceChange = (e) => {
    const filtered = gatewaysData?.filter((g) => g?.value !== e?.value) ?? [];
    setFailureInstanceOptions(filtered);

    const currentFailure = form.getFieldValue('failure_instance_id');
    const currentIds = Array.isArray(currentFailure) ? currentFailure?.map((item) => item?.value ?? item) : currentFailure?.value ? [currentFailure?.value] : [];

    if (currentIds?.includes(e?.value)) {
      const remaining = Array.isArray(currentFailure) ? currentFailure?.filter((item) => (item?.value ?? item) !== e?.value) : undefined;
      form.setFieldValue('failure_instance_id', remaining?.length ? remaining : undefined);
    }
  };
  const handleAddFields = (key, data) => {
    if (key === 'EXCHANGE_RATE' && data?.target?.checked && !formFieldsOptions.LOCATION) {
      return;
    }
    const numberOfActiveFields = Object.values(formFieldsOptions)?.filter((value) => value === true)?.length;
    if (!data?.target?.checked && (numberOfActiveFields === 1 || (numberOfActiveFields === 2 && key === 'LOCATION' && formFieldsOptions.EXCHANGE_RATE))) {
      return;
    }
    setFormFieldsOptions((pre) => ({ ...pre, [key]: data?.target?.checked, ...(key === 'LOCATION' && !data?.target?.checked && { EXCHANGE_RATE: false }) }));
  };
  const handleCreateRule = async (data) => {
    setLoading(true);
    const {
      instance_id,
      name,
      failure_instance_id,
      enabled,
      payment_retry_strategy,
      currency_data,
      location_data,
      bins_data,
      amount_value,
      operator,
      rangeFrom,
      rangeTo,
      rule_attributes,
      exchange_rate_amount,
      payment_currency,
      order_currency,
      is_payment_base_conversion,
    } = data;

    const on_failure_instance_id = Array.isArray(failure_instance_id) ? failure_instance_id?.map((data) => data?.value) : failure_instance_id?.value ? [failure_instance_id?.value] : [];
    const bins = Array.isArray(bins_data) && Object.prototype.toString.call(bins_data[0]) !== '[object String]' ? bins_data?.map((data) => data?.value) : bins_data;
    const location = location_data?.map((data) => data?.value);
    const currency = currency_data?.map((data) => data?.value);
    const attributes = Array.isArray(rule_attributes) && Object.prototype.toString.call(rule_attributes[0]) !== '[object String]' ? rule_attributes?.map((data) => data?.value) : rule_attributes;
    const payload = {
      ...(formFieldsOptions?.AMOUNT && {
        amount: {
          operator: operator?.value || 'less',
          ...(operator?.value === 'range' ? { rangeFrom: Number(rangeFrom), rangeTo: Number(rangeTo) } : { value: Number(amount_value) }),
        },
      }),
      ...(formFieldsOptions?.CURRENCY && { currency }),
      ...(formFieldsOptions?.LOCATION && { location, is_location_excluded: isLocationExcluded }),
      ...(formFieldsOptions?.BINS && { bins }),
      ...(formFieldsOptions?.ATTRIBUTES && { attributes }),
      ...(payment_retry_strategy?.value && { payment_retry_strategy: payment_retry_strategy?.value }),
      ...(formFieldsOptions?.EXCHANGE_RATE && {
        exchange_rate: {
          order_currency: order_currency?.value || order_currency,
          payment_currency: payment_currency?.value || payment_currency,
          amount: Number(exchange_rate_amount),
          is_payment_base_conversion,
        },
      }),
      instance_id: instance_id?.value,
      name,
      on_failure_instance_id,
      enabled,
    };
    const response = await createRule(store?._id, payload);
    if (response) {
      notification.success({ message: 'RULE CREATED SUCCESSFULLY', placement: 'bottomRight' });
      handleClose();
      updateRuleData(response);
    }
    setLoading(false);
  };
  const handleUpdateRuleData = async (data) => {
    setLoading(true);
    const {
      instance_id,
      name,
      failure_instance_id,
      enabled,
      payment_retry_strategy,
      currency_data,
      location_data,
      bins_data,
      amount_value,
      operator,
      rangeFrom,
      rangeTo,
      rule_attributes,
      payment_currency,
      order_currency,
      exchange_rate_amount,
    } = data;

    const orderCurrency = (order_currency?.value || order_currency)?.toLowerCase();
    const paymentCurrency = (payment_currency?.value || payment_currency)?.toLowerCase();
    const on_failure_instance_id = Array.isArray(failure_instance_id)
      ? failure_instance_id?.map((item) => item?.value ?? item).filter(Boolean)
      : failure_instance_id?.value
        ? [failure_instance_id.value]
        : [];
    const initialFailureIds = ruleData?.on_failure_instance_id ?? [];
    const amountOperator = operator?.value || ruleData?.amount?.operator || 'less';
    const banksBins = Array.isArray(bins_data) && Object.prototype?.toString.call(bins_data[0]) !== '[object String]' ? bins_data?.map((data) => data?.value) : bins_data;
    const customAttributes =
      Array.isArray(rule_attributes) && Object.prototype?.toString?.call(rule_attributes[0]) !== '[object String]' ? rule_attributes?.map((data) => data?.value) : rule_attributes;
    let payload = {};
    if (instance_id?.value && instance_id?.value !== initialFormData?.instance_id) {
      payload.instance_id = instance_id?.value;
    }
    if (payment_retry_strategy?.value && payment_retry_strategy?.value !== initialFormData?.payment_retry_strategy) {
      payload.payment_retry_strategy = payment_retry_strategy?.value;
    }
    if (name !== initialFormData?.name) {
      payload.name = name;
    }
    if (enabled !== initialFormData?.enabled) {
      payload.enabled = enabled;
    }
    if (isLocationExcluded !== ruleData?.is_location_excluded) {
      payload.is_location_excluded = isLocationExcluded;
    }
    if (!checkIsArraysAreSame(on_failure_instance_id, initialFailureIds)) {
      payload.on_failure_instance_id = on_failure_instance_id;
      if (formFieldsOptions?.EXCHANGE_RATE && on_failure_instance_id?.length === 0) {
        payload.exchange_rate = {
          order_currency: orderCurrency,
          payment_currency: paymentCurrency,
          amount: Number(exchange_rate_amount),
          is_payment_base_conversion: isPaymentBaseConversion,
        };
      }
    }
    if (
      formFieldsOptions?.AMOUNT &&
      (amountOperator !== initialFormData?.operator || amount_value !== ruleData?.amount?.value || rangeFrom !== ruleData?.amount?.rangeFrom || rangeTo !== ruleData?.amount?.rangeTo)
    ) {
      payload.amount = {
        operator: amountOperator,
        ...(amountOperator === 'range' ? { rangeFrom: Number(rangeFrom), rangeTo: Number(rangeTo) } : { value: Number(amount_value) }),
      };
    } else if (!formFieldsOptions?.AMOUNT && initialFormData?.operator && !amount_value && !rangeFrom && !rangeTo) {
      payload.amount = null;
    }
    if (formFieldsOptions?.BINS && Array.isArray(banksBins) && !checkIsArraysAreSame(banksBins, initialFormData?.bins_data)) {
      payload.bins = banksBins;
    } else if (!formFieldsOptions?.BINS && initialFormData?.bins_data?.length > 0 && !bins_data) {
      payload.bins = null;
    }
    if (formFieldsOptions?.LOCATION && Array.isArray(location_data) && Object.prototype.toString.call(location_data[0]) !== '[object String]') {
      payload.location = location_data?.map((data) => data?.value);
    } else if (!formFieldsOptions?.LOCATION && initialFormData?.location_data?.length > 0 && !location_data) {
      payload.location = null;
      if (initialFormData?.exchange_rate_amount) {
        payload.exchange_rate = null;
      }
    }
    if (formFieldsOptions?.CURRENCY && Array.isArray(currency_data) && Object.prototype.toString.call(currency_data[0]) !== '[object String]') {
      payload.currency = currency_data?.map((data) => data?.value);
    } else if (!formFieldsOptions?.CURRENCY && initialFormData?.currency_data?.length > 0 && !currency_data) {
      payload.currency = null;
    }
    if (formFieldsOptions?.ATTRIBUTES && Array.isArray(customAttributes) && !checkIsArraysAreSame(customAttributes, initialFormData?.rule_attributes)) {
      payload.attributes = customAttributes;
    } else if (!formFieldsOptions?.ATTRIBUTES && initialFormData?.rule_attributes?.length > 0 && !rule_attributes) {
      payload.attributes = null;
    }
    if (
      formFieldsOptions?.EXCHANGE_RATE &&
      (exchange_rate_amount !== ruleData?.exchange_rate?.amount ||
        orderCurrency !== ruleData?.exchange_rate?.order_currency ||
        paymentCurrency !== ruleData?.exchange_rate?.payment_currency ||
        isPaymentBaseConversion !== ruleData?.exchange_rate?.is_payment_base_conversion)
    ) {
      payload.exchange_rate = {
        order_currency: orderCurrency,
        payment_currency: paymentCurrency,
        amount: Number(exchange_rate_amount),
        is_payment_base_conversion: isPaymentBaseConversion,
      };
    } else if (!formFieldsOptions?.EXCHANGE_RATE && ruleData?.exchange_rate?.amount) {
      payload.exchange_rate = null;
    }
    if (Object.keys(payload)?.length > 0) {
      const response = await updateRule(ruleData?._id, store?._id, payload);
      if (response) {
        notification.success({ message: 'RULE UPDATED SUCCESSFULLY', placement: 'bottomRight' });
        handleClose();
        updateRuleData(response);
      }
    } else {
      notification.error({ message: 'No changes detected. Please modify the data before submitting', placement: 'bottomRight' });
    }
    setLoading(false);
  };
  const addCustomAttributes = (e) => {
    if (/^[^%?!{}[\]$^*@<>]*$/.test(customOptionValue)) {
      e.preventDefault();
      setAttributesOptions((pre) => [...pre, { value: customOptionValue, label: customOptionValue }]);
      const currentSelectedValues = form.getFieldValue('rule_attributes');
      let newSelectedValues = [];
      if (Array.isArray(currentSelectedValues) && Object.prototype.toString.call(currentSelectedValues[0]) !== '[object String]') {
        newSelectedValues = currentSelectedValues?.map((data) => data?.value) || [];
      } else if (currentSelectedValues?.length > 0) {
        newSelectedValues = currentSelectedValues;
      }
      newSelectedValues?.push(customOptionValue);
      form.setFieldValue('rule_attributes', newSelectedValues);
      setCustomOptionValue('');
      setTimeout(() => {
        customOptionInputRef?.current?.focus();
      }, 0);
    } else {
      notification.error({ message: 'Attribute value contains invalid special characters', placement: 'bottomRight' });
    }
  };
  const addCustomBinValue = (e) => {
    if (Number(customOptionValue) && customOptionValue?.length > 5) {
      e.preventDefault();
      setBinsOptions((pre) => [...pre, { value: customOptionValue, label: customOptionValue }]);
      const currentSelectedValues = form.getFieldValue('bins_data');
      let newSelectedValues = [];
      if (Array.isArray(currentSelectedValues) && Object.prototype.toString.call(currentSelectedValues[0]) !== '[object String]') {
        newSelectedValues = currentSelectedValues?.map((data) => data?.value) || [];
      } else if (currentSelectedValues?.length > 0) {
        newSelectedValues = currentSelectedValues;
      }
      newSelectedValues?.push(customOptionValue);
      form.setFieldValue('bins_data', newSelectedValues);
      setCustomOptionValue('');
      setTimeout(() => {
        customOptionInputRef?.current?.focus();
      }, 0);
    }
  };
  const amount_condition_type = Form.useWatch('operator', form)?.value || ruleData?.amount?.operator;

  const orderCurrency = Form.useWatch('order_currency', form)?.value || ruleData?.exchange_rate?.order_currency?.toUpperCase() || 'PKR';
  const paymentCurrency = Form.useWatch('payment_currency', form)?.value || ruleData?.exchange_rate?.payment_currency?.toUpperCase() || 'USD';
  const orderCurrencyFilteredOptions = useMemo(() => {
    return SUB_CurrencyNames.filter((opt) => opt?.value !== paymentCurrency);
  }, [paymentCurrency]);
  const paymentCurrencyFilteredOptions = useMemo(() => {
    return SUB_CurrencyNames.filter((opt) => opt?.value !== orderCurrency);
  }, [orderCurrency]);
  return (
    <div style={{ width: '100%' }}>
      <div className='routing-form-fields-header'>
        <span style={{ marginRight: '10px', fontWeight: 'bold' }}>Define Rule Using: </span>
        <div className={formFieldsOptions?.AMOUNT ? 'routing-form-fields-checked' : 'routing-form-fields-unchecked'}>
          <Checkbox checked={formFieldsOptions?.AMOUNT} onChange={(e) => handleAddFields('AMOUNT', e)}>
            Amount
          </Checkbox>
        </div>
        <div className={formFieldsOptions?.CURRENCY ? 'routing-form-fields-checked' : 'routing-form-fields-unchecked'}>
          <Checkbox checked={formFieldsOptions?.CURRENCY} onChange={(e) => handleAddFields('CURRENCY', e)}>
            Currency
          </Checkbox>
        </div>
        <div className={formFieldsOptions?.BINS ? 'routing-form-fields-checked' : 'routing-form-fields-unchecked'}>
          <Checkbox checked={formFieldsOptions?.BINS} onChange={(e) => handleAddFields('BINS', e)}>
            Bins
          </Checkbox>
        </div>
        <div className={formFieldsOptions?.ATTRIBUTES ? 'routing-form-fields-checked' : 'routing-form-fields-unchecked'}>
          <Checkbox checked={formFieldsOptions?.ATTRIBUTES} onChange={(e) => handleAddFields('ATTRIBUTES', e)}>
            Attributes
          </Checkbox>
        </div>
        <div className={formFieldsOptions?.LOCATION ? 'routing-form-fields-checked' : 'routing-form-fields-unchecked'}>
          <Checkbox checked={formFieldsOptions?.LOCATION} onChange={(e) => handleAddFields('LOCATION', e)}>
            Location
          </Checkbox>
        </div>
        <Tooltip title={'Location must be selected to configure exchange rate'}>
          <div className={formFieldsOptions?.EXCHANGE_RATE ? 'routing-form-fields-checked' : 'routing-form-fields-unchecked'}>
            <Checkbox checked={formFieldsOptions?.EXCHANGE_RATE} onChange={(e) => handleAddFields('EXCHANGE_RATE', e)} disabled={!formFieldsOptions?.LOCATION}>
              Exchange Rate
            </Checkbox>
          </div>
        </Tooltip>
      </div>
      <Form layout='vertical' onFinish={ruleData ? handleUpdateRuleData : handleCreateRule} form={form} initialValues={initialFormData}>
        <div className='routing-form'>
          <div className='form-container'>
            <Form.Item
              className='w-full steric-after'
              name='name'
              label={'Name'}
              rules={[
                {
                  required: true,
                  message: 'Name is required',
                },
              ]}>
              <Input placeholder={'Enter rule name'} />
            </Form.Item>
            <Form.Item className='w-full' label='Payment Retry Strategy' name={'payment_retry_strategy'}>
              <Select optionsValue={paymentRetryStrategyOptions} />
            </Form.Item>
            <Form.Item
              className='w-full steric-after'
              name={'instance_id'}
              label={'Instance Id'}
              rules={[
                {
                  required: true,
                  message: 'Instance Id is required',
                },
              ]}>
              <Select
                loading={isGettingGatewaysData}
                placeholder={'Select instance id'}
                allowClear={true}
                showSearch={true}
                optionsValue={instanceOptions}
                onClear={handleInstanceChange}
                onSelect={handleInstanceChange}
              />
            </Form.Item>
            <Form.Item
              className={`w-full ${formFieldsOptions?.EXCHANGE_RATE ? '' : 'steric-after'}`}
              name={'failure_instance_id'}
              label={
                <>
                  Failure Instance Id
                  <Tooltip title={'You can select more then one failure instances'}>
                    <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                  </Tooltip>
                </>
              }
              rules={[
                {
                  required: !formFieldsOptions?.EXCHANGE_RATE,
                  message: 'Failure Instance Id is required',
                },
              ]}>
              <Select
                mode={failureInstanceOptions?.length > 1 ? 'multiple' : undefined}
                placeholder={'Select failure instance id'}
                disabled={!!!Form.useWatch('instance_id', form)}
                loading={isGettingGatewaysData}
                allowClear={true}
                showSearch={true}
                optionsValue={failureInstanceOptions}
              />
            </Form.Item>
            {formFieldsOptions?.AMOUNT && (
              <div className={amount_condition_type === 'range' ? 'three-width-row' : 'two-width-row'}>
                <Form.Item className='w-full' label='Amount Condition' name={'operator'}>
                  <Select optionsValue={amountOptions} />
                </Form.Item>
                {amount_condition_type === 'range' ? (
                  <>
                    <Form.Item
                      className='w-full steric-after'
                      label='Amount Range From'
                      name={'rangeFrom'}
                      rules={[
                        {
                          required: true,
                          message: 'Range value is required',
                        },
                      ]}>
                      <Input type={'number'} placeholder={'Enter amount range from'} min={1} />
                    </Form.Item>
                    <Form.Item
                      className='w-full steric-after'
                      label='Amount Range To'
                      name={'rangeTo'}
                      rules={[
                        {
                          required: true,
                          message: 'Range value is required',
                        },
                      ]}>
                      <Input type={'number'} placeholder={'Enter amount range to'} min={1} />
                    </Form.Item>
                  </>
                ) : (
                  <Form.Item
                    className='w-full steric-after'
                    label='Amount Value'
                    name='amount_value'
                    rules={[
                      {
                        required: true,
                        message: 'Amount Value is required',
                      },
                    ]}>
                    <Input type={'number'} placeholder={'Enter amount value'} min={1} />
                  </Form.Item>
                )}
              </div>
            )}
            {formFieldsOptions?.CURRENCY && (
              <Form.Item
                className='w-full steric-after'
                label='Currency'
                name={'currency_data'}
                rules={[
                  {
                    required: true,
                    message: 'Currency is required',
                  },
                ]}>
                <Select mode='multiple' placeholder={'Select currency'} allowClear={true} optionsValue={SUB_CurrencyNames} />
              </Form.Item>
            )}
            {formFieldsOptions?.LOCATION && (
              <Form.Item label='Location'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Form.Item name='location_data' rules={[{ required: true, message: 'Location is required' }]} noStyle>
                    <Select mode='multiple' placeholder='Select location' allowClear options={locationsOptions} />
                  </Form.Item>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Switch checked={isLocationExcluded} onChange={setIsLocationExcluded} />
                    <span>Exclude Selected</span>
                  </div>
                </div>
              </Form.Item>
            )}
            {formFieldsOptions?.BINS && (
              <Form.Item
                className='w-full steric-after'
                label='Bank Bins'
                name={'bins_data'}
                rules={[
                  {
                    required: true,
                    message: 'Bins is required',
                  },
                ]}>
                <Select
                  mode='multiple'
                  showSearch={true}
                  getPopupContainer={(triggerNode) => triggerNode?.parentElement}
                  filterOption={(input, option) => (option?.label ?? '')?.toLowerCase()?.includes(input?.toLowerCase()) || (option?.value ?? '')?.toLowerCase()?.includes(input?.toLowerCase())}
                  allowClear={true}
                  placeholder={'Select bins'}
                  onDropdownVisibleChange={() => setCustomOptionValue('')}
                  dropdownRender={(menu) => (
                    <>
                      {binsOptions && binsOptions?.length > 0 ? menu : <div style={{ padding: '8px', color: 'gray' }}>No bins available. Please create a custom bin below.</div>}
                      <Divider
                        style={{
                          margin: '5px 0',
                        }}
                      />
                      <div className='attribute-container'>
                        <Input
                          placeholder='Enter bin number'
                          ref={customOptionInputRef}
                          value={customOptionValue}
                          onChange={(event) => {
                            if ((/^[0-9]*$/.test(event?.target?.value) && event?.target?.value?.length < 9) || !event?.target?.value) {
                              setCustomOptionValue(event?.target?.value);
                            }
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                        <Button className={`primaryBtn rounded-lg rule-btn attribute-btn ${customOptionValue?.length < 6 ? 'disable-btn' : ''}`} icon={<PlusOutlined />} onClick={addCustomBinValue}>
                          Add
                        </Button>
                      </div>
                    </>
                  )}
                  optionsValue={binsOptions}
                />
              </Form.Item>
            )}
            {formFieldsOptions?.ATTRIBUTES && (
              <Form.Item
                className='w-full steric-after'
                label='Attributes'
                name={'rule_attributes'}
                rules={[
                  {
                    required: true,
                    message: 'Attribute is required',
                  },
                ]}>
                <Select
                  mode='multiple'
                  showSearch={true}
                  getPopupContainer={(triggerNode) => triggerNode.parentElement}
                  filterOption={(input, option) => (option?.label ?? '')?.toLowerCase()?.includes(input?.toLowerCase()) || (option?.value ?? '')?.toLowerCase()?.includes(input?.toLowerCase())}
                  allowClear={true}
                  placeholder={'Select attributes'}
                  onDropdownVisibleChange={() => setCustomOptionValue('')}
                  dropdownRender={(menu) => (
                    <>
                      {attributesOptions && attributesOptions?.length > 0 ? (
                        menu
                      ) : (
                        <div style={{ padding: '8px', color: 'gray' }}>No attributes available. Please create a custom attribute below.</div>
                      )}
                      <Divider
                        style={{
                          margin: '5px 0',
                        }}
                      />
                      <div className='attribute-container'>
                        <Input
                          placeholder='Enter custom attribute'
                          ref={customOptionInputRef}
                          value={customOptionValue}
                          onChange={(event) => setCustomOptionValue(event?.target?.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                        <Button
                          className={`primaryBtn rounded-lg rule-btn attribute-btn ${customOptionValue?.length < 1 ? 'disable-btn' : ''}`}
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            if (customOptionValue?.length > 0) {
                              addCustomAttributes(e);
                            }
                          }}>
                          Add
                        </Button>
                      </div>
                    </>
                  )}
                  optionsValue={attributesOptions}
                />
              </Form.Item>
            )}
            {formFieldsOptions?.EXCHANGE_RATE && (
              <div className={'three-width-row'}>
                <Form.Item className='w-full' label='Order Currency' name={'order_currency'}>
                  <Select options={orderCurrencyFilteredOptions} showSearch={true} />
                </Form.Item>
                <Form.Item label='Exchange Rate Amount' required>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Form.Item
                      name='exchange_rate_amount'
                      rules={[
                        {
                          required: true,
                          message: 'Exchange Rate Amount is required',
                        },
                        {
                          validator: (_, value) => {
                            if (value > 0) {
                              return Promise.resolve();
                            } else if (value) {
                              return Promise.reject('Value must be a positive number');
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      noStyle>
                      <Input
                        type={'number'}
                        placeholder={isPaymentBaseConversion ? `Enter how many ${paymentCurrency} equal to 1 ${orderCurrency}` : `Enter how many ${orderCurrency} equal to 1 ${paymentCurrency}`}
                      />
                    </Form.Item>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Switch checkedChildren={paymentCurrency} unCheckedChildren={orderCurrency} checked={isPaymentBaseConversion} onChange={setIsPaymentBaseConversion} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                        <span>Conversion Base</span>
                        <Tooltip
                          title={
                            isPaymentBaseConversion
                              ? `Specify how many ${paymentCurrency} equal 1 ${orderCurrency}. Formula: (order_amount*exchange_rate_amount)`
                              : `Specify how many ${orderCurrency} equal 1 ${paymentCurrency}. Formula: (order_amount/exchange_rate_amount)`
                          }>
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </Form.Item>
                <Form.Item className='w-full' label='Payment Currency' name={'payment_currency'}>
                  <Select options={paymentCurrencyFilteredOptions} showSearch={true} />
                </Form.Item>
              </div>
            )}
          </div>
          <Form.Item name='enabled' className='modal-field' label='Rule Status' valuePropName='checked'>
            <Switch defaultChecked />
          </Form.Item>
          <div className='btn-container'>
            <div className='rule-btn'>
              <ANTDButton className='cancel-btn' onClick={handleClose}>
                Cancel
              </ANTDButton>
              <Button loading={isLoading} className='submit-btn button-theme' htmlType='submit'>
                {ruleData ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default RoutingRuleForm;
